import React from "react";
import { userCanContribute } from "../../utils/userRoles";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import { useProductGuide } from "./GuideContext";
import { journeyStepsForChapter } from "./guideProgress";

/**
 * Compact 4-step orientation rail in shell chrome (above processing banner).
 * Visible while the first-run guide is active. GuideStrip keeps the CTA.
 */
export default function JourneyRail() {
  const guide = useProductGuide();
  const { isMobile } = useDeviceDetection();

  const canContribute = (() => {
    try {
      return userCanContribute(JSON.parse(localStorage.getItem("user") || "{}"));
    } catch {
      return false;
    }
  })();

  if (!guide || !canContribute || !guide.active || !guide.chapter) return null;

  const steps = journeyStepsForChapter(guide.chapter.id);
  const currentIdx = steps.findIndex((s) => s.status === "current");

  const onStepClick = (step) => {
    if (step.status !== "done") return;
    if (typeof guide.goToChapter !== "function") return;
    guide.goToChapter(step.entryChapterId);
    // Pass override so resume uses the target chapter before state flushes.
    try {
      guide.resumeGuide?.(step.entryChapterId);
    } catch {
      /* ignore */
    }
  };

  return (
    <nav
      className={
        isMobile
          ? "border-t border-slate-200/80 bg-white/95 px-3 py-1.5"
          : "border-t border-slate-200/80 bg-white/95 px-4 py-2 sm:px-6"
      }
      aria-label="First-run journey"
    >
      <ol className="mx-auto flex max-w-3xl items-center justify-between gap-1 sm:justify-center sm:gap-0">
        {steps.map((step, idx) => {
          const isDone = step.status === "done";
          const isCurrent = step.status === "current";
          const clickable = isDone;
          return (
            <li key={step.id} className="flex min-w-0 flex-1 items-center sm:flex-none">
              {idx > 0 ? (
                <span
                  className={
                    idx <= currentIdx
                      ? "mx-1 hidden h-px w-6 shrink-0 bg-emerald-500 sm:block sm:w-10"
                      : "mx-1 hidden h-px w-6 shrink-0 bg-slate-200 sm:block sm:w-10"
                  }
                  aria-hidden
                />
              ) : null}
              <button
                type="button"
                disabled={!clickable}
                onClick={() => onStepClick(step)}
                title={
                  clickable
                    ? `Go back to ${step.label}`
                    : isCurrent
                      ? `Current: ${step.label}`
                      : step.label
                }
                aria-current={isCurrent ? "step" : undefined}
                className={
                  isCurrent
                    ? "blink-soft-slow flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md bg-emerald-800 px-2 py-1 text-xs font-semibold text-white sm:flex-none sm:px-3"
                    : isDone
                      ? "flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-50 sm:flex-none sm:px-3"
                      : "flex min-w-0 flex-1 cursor-default items-center justify-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-slate-400 sm:flex-none sm:px-3"
                }
              >
                <span
                  className={
                    isCurrent
                      ? "flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white/20 text-[10px] font-bold tabular-nums"
                      : isDone
                        ? "flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-bold text-emerald-800 tabular-nums"
                        : "flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-400 tabular-nums"
                  }
                  aria-hidden
                >
                  {isDone ? "✓" : idx + 1}
                </span>
                <span className="truncate">{step.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
